import { LessonCourseDefaultThumbnail } from '@/constant/imgImport';
import Skeleton from '@mui/material/Skeleton';
import { Button } from 'design-system/components/Button';
import { Divider } from 'design-system/components/Divider';
import dynamic from 'next/dynamic';
import { ComponentPropsWithoutRef } from 'react-markdown/lib/ast-to-react';
import { twMerge } from 'tailwind-merge';
import CoursePaymentCardThumbnail from './components/CoursePaymentCardThumbnail';
import { ECoursePaymentCardPlacement, TPaymentCardCourse } from './types';

const CoursePaymentCardInProgress = dynamic(
  () => import('./components/variants/CoursePaymentCardInProgress')
);
const CoursePaymentCardSlider = dynamic(
  () => import('./components/variants/CoursePaymentCardSlider')
);

const CoursePaymentCard = (props: TPaymentCardCourse) => {
  const hashVariantComponents = {
    [ECoursePaymentCardPlacement.AtFeed]: <></>,
    [ECoursePaymentCardPlacement.PurchaseHistory]: <></>,
    [ECoursePaymentCardPlacement.InCart]: <></>,
    [ECoursePaymentCardPlacement.FinishedCourse]: <></>,
    [ECoursePaymentCardPlacement.Groups]: <></>,
    [ECoursePaymentCardPlacement.MyCourse]: <></>,
    [ECoursePaymentCardPlacement.Slider]: (
      <CoursePaymentCardSlider {...props} />
    ),
    [ECoursePaymentCardPlacement.Studying]: <></>,
    [ECoursePaymentCardPlacement.CoursePayment]: <></>,
    [ECoursePaymentCardPlacement.InProgress]: (
      <CoursePaymentCardInProgress {...props} />
    ),
  };

  return hashVariantComponents[props.placement];
};

export default CoursePaymentCard;

export const CoursePaymentCardSkeleton = ({
  placement,
  ...props
}: {
  placement: TPaymentCardCourse['placement'];
} & ComponentPropsWithoutRef<'div'>) => {
  // // using switch case because just some placement need another skeleton, rest of them seem to be the same
  switch (placement) {
    case ECoursePaymentCardPlacement.Slider:
      return (
        <div
          {...props}
          className={twMerge(
            'flex h-auto w-[275px] cursor-pointer flex-col overflow-hidden rounded-[10px] border border-Other/Outlinedborder-stroke shadow-elevator2',
            props.className
          )}
        >
          <div className="flex flex-shrink-0 flex-col gap-3">
            <CoursePaymentCardThumbnail
              thumbnail={LessonCourseDefaultThumbnail}
            />
          </div>

          <div className="flex h-auto flex-col gap-[10px] p-3">
            <Skeleton variant="rounded" width={'100%'} height={28} />
            <Skeleton variant="rounded" width={'100%'} height={100} />
            <Divider />
            <Skeleton variant="rounded" width={'100%'} height={20} />
          </div>
        </div>
      );

    case ECoursePaymentCardPlacement.Studying:
      return (
        <div
          {...props}
          className={twMerge(
            'flex h-auto w-[342px] cursor-pointer items-center gap-3 overflow-hidden rounded-[10px] border border-Other/Outlinedborder-stroke p-2 shadow-elevator2',
            props.className
          )}
        >
          <div className="flex-shrink-0">
            <CoursePaymentCardThumbnail
              thumbnail={LessonCourseDefaultThumbnail}
              className="!h-[72px]"
            />
          </div>
          <div className="flex h-auto flex-1 flex-col gap-[10px]">
            <Skeleton variant="rounded" width={'100%'} height={16} />
            <div className="flex items-center gap-4">
              <Skeleton variant="rounded" width={'100%'} height={16} />
            </div>
          </div>
          <div>
            <Button
              emphasis="medium"
              mode="icon-only"
              size="small"
              icon={{
                name: 'arrow-left-md',
              }}
            />
          </div>
        </div>
      );

    default:
      return (
        <div
          {...props}
          className={twMerge(
            `flex w-full max-w-[361px] cursor-pointer flex-col gap-4 overflow-hidden rounded-[10px] border border-Other/Outlinedborder-stroke p-3 shadow-elevator2 xl:max-w-none xl:flex-row xl:p-4`,
            props.className
          )}
        >
          <div className="flex flex-shrink-0 flex-col gap-3">
            <CoursePaymentCardThumbnail
              thumbnail={LessonCourseDefaultThumbnail}
            />
          </div>

          <div className="flex h-auto flex-1 flex-col gap-2">
            <Skeleton variant="rounded" width={'100%'} height={28} />
            <Skeleton variant="rounded" width={'100%'} height={100} />
          </div>
        </div>
      );
  }
};
